import { useRef, useState } from "react";
import { Button, Divider, IconButton, Menu, MenuItem, MoreVertIcon, Stack, Tooltip } from "@app/ui-kit";

export type RecordAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Why the action is unavailable, or what it does. Shown on hover and focus; a disabled action should always have one. */
  tooltip?: string;
};

export type RecordMenuAction = RecordAction & {
  /** Destructive actions render in error colour, sit last, and never become the primary. */
  destructive?: boolean;
  /** A divider before this item. */
  divider?: boolean;
};

type RecordActionsProps = {
  /** The action the page is most often opened to do. Omitted in a table row, where the menu is the whole control. */
  primary?: RecordAction;
  /** Everything else, behind "More actions". Nothing renders when empty. */
  items?: RecordMenuAction[];
  /** Called when the menu opens, for a caller that has to look something up before it knows what to offer. */
  onOpen?: () => void;
};

/**
 * ACT-1: a record page's actions, ranked. One contained primary, the action the page is most often
 * opened to do, and an overflow for the occasional and destructive ones. Built here rather than in
 * @app/ui-kit: the admin console is its first consumer, and promotion is the second consumer's story.
 *
 * The menu closes before an item's action runs, so a confirmation dialog never opens under it.
 * A disabled item stays visible and says why in its tooltip; it is never hidden.
 */
export const RecordActions = ({ primary, items = [], onOpen }: RecordActionsProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const pending = useRef<(() => void) | null>(null);

  const ordered = [...items.filter((item) => !item.destructive), ...items.filter((item) => item.destructive)];
  const firstDestructive = ordered.findIndex((item) => item.destructive);

  const choose = (action: () => void) => {
    pending.current = action;
    setAnchorEl(null);
  };

  const runPending = () => {
    const action = pending.current;
    pending.current = null;
    action?.();
  };

  const primaryButton = primary ? (
    <Button variant="contained" onClick={primary.onClick} disabled={primary.disabled} sx={{ flexGrow: { xs: 1, md: 0 } }}>
      {primary.label}
    </Button>
  ) : null;

  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      {primary?.tooltip ? (
        <Tooltip title={primary.tooltip}>
          {/* A disabled button fires no events, so the tooltip listens on a wrapper. */}
          <span style={{ display: "flex", flexGrow: 1 }}>{primaryButton}</span>
        </Tooltip>
      ) : (
        primaryButton
      )}
      {ordered.length > 0 && (
        <>
          <IconButton
            aria-label="More actions"
            aria-haspopup="menu"
            aria-expanded={anchorEl ? "true" : undefined}
            onClick={(event) => { onOpen?.(); setAnchorEl(event.currentTarget); }}
            sx={{ color: "text.primary" }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            TransitionProps={{ onExited: runPending }}
            // Arrow keys reach a disabled item too, so a keyboard user can read its reason.
            MenuListProps={{ disabledItemsFocusable: true }}
          >
            {ordered.map((item, index) => {
              const dividerBefore = index > 0 && (item.divider || index === firstDestructive);
              const menuItem = (
                <MenuItem
                  key={item.label}
                  aria-disabled={item.disabled || undefined}
                  onClick={item.disabled ? undefined : () => choose(item.onClick)}
                  sx={{
                    minWidth: 200,
                    color: item.disabled ? "text.disabled" : item.destructive ? "error.main" : "text.primary",
                    cursor: item.disabled ? "default" : "pointer"
                  }}
                >
                  {item.label}
                </MenuItem>
              );
              return [
                dividerBefore ? <Divider key={`${item.label}-divider`} /> : null,
                item.tooltip ? (
                  <Tooltip key={item.label} title={item.tooltip} placement="left">
                    {menuItem}
                  </Tooltip>
                ) : (
                  menuItem
                )
              ];
            })}
          </Menu>
        </>
      )}
    </Stack>
  );
};
